import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardMedia,
  CardContent,
} from "@mui/material";
import Footer from "../../../../common/Footer";
import Header from "../../../../common/Header";
import { useParams } from "react-router-dom";

import { config } from "../../../../config/config";
import { apiList, invokeApi } from "../../../../services/apiServices";
import { useCookies } from "react-cookie";

// ========== Styled Components ==========
const BannerImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
});

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: "bold",
  fontSize: "1rem",
  color: "#1d1a57",
  borderBottom: "2px solid #1d1a57",
}));

const PublicationDetailPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { ogUrl } = useParams(); // Using ogUrl to determine category: 'journal-articles' or 'reports-policy-briefs'
  const [cookies] = useCookies();

  const [articles, setArticles] = useState([]);
  const [pdfs, setPdfs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Determine view mode based on URL
  const isReportsView = ogUrl === "reports-policy-briefs";
  const isArticlesView = ogUrl === "journal-articles";

  // ===== Fetch Data =====
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        if (isArticlesView) {
          const response = await invokeApi(
            config.getMyCollege + apiList.getArticles,
            {},
            cookies
          );
          if (response?.status === 200 && response?.data?.articles) {
            setArticles(response.data.articles);
          } else {
            // setError("Failed to fetch articles.");
            setArticles([]); // Allow empty state
          }
        } else if (isReportsView) {
          const response = await invokeApi(
            config.getMyCollege + apiList.getPdfPublications,
            {},
            cookies
          );
          if (response?.status === 200 && response?.data?.pdfs) {
            setPdfs(response.data.pdfs);
          } else {
            // setError("Failed to fetch reports.");
            setPdfs([]); // Allow empty state
          }
        }
      } catch (err) {
        console.error("Error fetching publication data:", err);
        setError("Something went wrong while fetching data.");
      } finally {
        setLoading(false);
      }
    };

    if (isArticlesView || isReportsView) {
      fetchData();
    } else {
      setLoading(false);
    }

    window.scrollTo(0, 0);
  }, [ogUrl, isArticlesView, isReportsView, cookies]);

  // ===== Render Helpers =====

  const renderArticlesTable = () => (
    <Box sx={{ mx: 'auto', maxWidth: '1200px', mt: 2 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: "bold", fontFamily: "Montserrat, sans-serif", textAlign: "center" }}>
        Journal Articles
      </Typography>
      <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e0e0e0", borderRadius: 0 }}>
        <Table sx={{ minWidth: 650 }} aria-label="articles table">
          <TableHead>
            <TableRow>
              <TableCell
                width="100px"
                sx={{
                  fontWeight: "bold",
                  borderRight: "1px solid #e0e0e0",
                  borderBottom: "1px solid #e0e0e0",
                  fontSize: "1rem",
                  color: "#000"
                }}
              >
                Year
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  borderBottom: "1px solid #e0e0e0",
                  fontSize: "1rem",
                  color: "#000"
                }}
              >
                Citation
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {articles.length > 0 ? (
              articles.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{ "&:last-child td, &:last-child th": { borderBottom: 0 } }}
                >
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{
                      verticalAlign: 'top',
                      fontWeight: '500',
                      borderRight: "1px solid #e0e0e0",
                      borderBottom: "1px solid #e0e0e0",
                      color: "#333"
                    }}
                  >
                    {row.year}
                  </TableCell>
                  <TableCell
                    sx={{
                      verticalAlign: 'top',
                      borderBottom: "1px solid #e0e0e0",
                      color: "#333",
                      fontSize: "0.95rem",
                      "& a": {
                        color: "#00838f", // Teal color from image
                        textDecoration: "underline",
                      }
                    }}
                  >
                    <div dangerouslySetInnerHTML={{ __html: row.content }} />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={2} align="center" sx={{ borderBottom: "1px solid #e0e0e0" }}>
                  No articles found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderReportsGrid = () => (
    <Box>
      <Typography variant="h4" sx={{ mb: 4, color: "#00796b", fontWeight: "bold", textAlign: 'center' }}>
        Material
      </Typography>
      <Grid container spacing={4} justifyContent="center">
        {pdfs.length > 0 ? (
          pdfs.map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item.id}> {/* md=3 for 4 items per row, more compact */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', height: '100%' }}>
                <a href={item.pdfUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit', width: '100%' }}>
                  <Box
                    sx={{
                      cursor: "pointer",
                      textAlign: "center",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      transition: "transform 0.3s ease, box-shadow 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        "& .MuiTypography-root": {
                          color: "#00796b"
                        }
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        aspectRatio: "1/1",
                        mb: 2,
                        borderRadius: "8px",
                        overflow: "hidden",
                        boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
                        border: "1px solid #eee",
                        backgroundColor: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                    >
                      <Box
                        component="img"
                        src={item.imageUrl}
                        alt={item.title}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover"
                        }}
                      />
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: "0.9rem",
                        fontWeight: 600,
                        lineHeight: 1.2,
                        px: 1,
                        color: "#333",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        minHeight: "2.4em"
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        color: "#00796b",
                        mt: 1
                      }}
                    >
                      Read Now
                    </Typography>
                  </Box>
                </a>
              </Box>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Typography align="center">No material found.</Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );

  // ===== Main Render =====
  return (
    <>
      <Header />
      <Grid
        container
        sx={{
          px: isMobile ? 2 : 4,
          py: isMobile ? 2 : 4,
          maxWidth: "100%",
          margin: "0 auto",
          mt: isMobile ? 10 : 8,
        }}
      >
        <Grid item xs={12} md={12} sx={{ pl: !isMobile ? 0 : 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {isArticlesView && renderArticlesTable()}
              {isReportsView && renderReportsGrid()}
              {!isArticlesView && !isReportsView && (
                <Typography variant="h6" align="center">
                  Please select a valid publication category.
                </Typography>
              )}
            </Box>
          )}
        </Grid>
      </Grid>
      <Footer variant="light" />
    </>
  );
};

export default PublicationDetailPage;
